import { useEffect, useState } from "react";
import { collection, onSnapshot, orderBy, query, limit } from "firebase/firestore";
import { db } from "../../../firebase";

/**
 * @typedef {Object} BookingDoc — see src/pages/crm/schema.js::defaultBooking
 * for the full nullable shape. Documents may be missing any field.
 */

/**
 * Single onSnapshot listener over bookings, ordered by submittedAt desc.
 * Capped at 500 docs (plenty of headroom — indie booking volume is <100/year).
 * Consumers partition client-side so no composite Firestore indexes needed.
 *
 * @returns {{ bookings: Array<BookingDoc & {id: string}>, loading: boolean, error: Error|null }}
 */
export function useBookings() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const q = query(collection(db, "bookings"), orderBy("submittedAt", "desc"), limit(500));
    const unsub = onSnapshot(
      q,
      (snap) => {
        // Test-flagged bookings flow through; BookingsList filters them out
        // by default and exposes a "Show test bookings" toggle for dev work.
        setBookings(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      (err) => {
        console.error("useBookings error:", err);
        setError(err);
        setLoading(false);
      }
    );
    return unsub;
  }, []);

  return { bookings, loading, error };
}
